"use client";

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useRouter } from "next/navigation";
import { createPortal } from "react-dom";
import { Search, X, ArrowLeft } from "lucide-react";
import type { SearchResult } from "@/lib/searchRegistry";
import SearchResults from "./SearchResults";

// The search registry statically imports posts.json (1.1 MB) + all comparison
// JSONs. A static import here would bundle that into the header chunk loaded
// on EVERY page — the dynamic import defers the download until the user
// actually interacts with search.
type SearchModule = typeof import("@/lib/searchRegistry");
let searchModulePromise: Promise<SearchModule> | null = null;
const loadSearchModule = () => (searchModulePromise ??= import("@/lib/searchRegistry"));

export default function GlobalSearch({ activeCountryCode }: { activeCountryCode?: string }) {
  const router = useRouter();
  
  // Search state
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isMobileOverlayOpen, setIsMobileOverlayOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  // Rotating placeholder terms from existing calculators, comparisons, blog guides & glossary terms
  // Localize placeholders based on active country
  const placeholders = useMemo(() => {
    const countrySpecific: Record<string, string[]> = {
      'nz': ["PIR...", "KiwiSaver...", "ESCT..."],
      'au': ["Superannuation...", "PAYG...", "Medicare Levy..."],
      'us': ["Roth IRA...", "401k...", "HSA..."],
      'gb': ["ISA...", "National Insurance...", "PAYE..."],
      'de': ["Einkommensteuer...", "Riester...", "Elterngeld..."],
      'ca': ["RRSP...", "TFSA...", "CPP..."],
      'sg': ["CPF...", "IRAS...", "COE..."],
      'in': ["PPF...", "EPF...", "Section 80C..."],
      'ch': ["Pillar 3a...", "AHV...", "Quellensteuer..."],
      'nl': ["Box 3...", "AOW...", "Zorgtoeslag..."],
    };
    
    const base = [
      "Income Tax Calculator...",
      "Mortgage Calculator...",
      "Compound Interest...",
      "50/30/20 Budget...",
      "Emergency Fund...",
    ];
    
    if (activeCountryCode && countrySpecific[activeCountryCode]) {
      return [...countrySpecific[activeCountryCode], ...base];
    }
    
    // Default: mix of global terms
    return [
      ...base,
      "Roth IRA vs 401k...",
      "Superannuation Guide...",
      "ISA...",
      "KiwiSaver...",
    ];
  }, [activeCountryCode]);

  const [placeholderIndex, setPlaceholderIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % placeholders.length);
    }, 5000); // cycle every 5 seconds
    return () => clearInterval(timer);
  }, [placeholders]);

  // Refs
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);

  // Set mounted state for portal
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Prevent body scroll when mobile overlay is open
  useEffect(() => {
    if (isMobileOverlayOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      return () => {
        // Restore body scroll
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        
        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isMobileOverlayOpen]);

  // 1. Lazy load search module + index on focus
  const handleFocus = () => {
    setIsOpen(true);
    if (results.length === 0 && !isLoading) {
      setIsLoading(true);
      loadSearchModule()
        .then((m) => m.buildSearchIndex())
        .catch((e) => console.error("Failed to build search index:", e))
        .finally(() => setIsLoading(false));
    }
  };

  // 2. Debounce query changes (300ms)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [query]);

  // 3. Perform search when debounced query changes (module loads on demand)
  useEffect(() => {
    if (debouncedQuery.trim().length >= 2) {
      let cancelled = false;
      loadSearchModule().then((m) => {
        if (cancelled) return;
        setResults(m.performSearch(debouncedQuery, activeCountryCode));
        setActiveIndex(-1); // Reset keyboard navigation index
      });
      return () => {
        cancelled = true;
      };
    } else {
      setResults([]);
      setActiveIndex(-1);
    }
  }, [debouncedQuery, activeCountryCode]);

  // 4. Flattened list of items currently shown (matches SearchResults.tsx slicing)
  const flatItems = useMemo(() => {
    const calculators = results.filter((r) => r.type === "calculator").slice(0, 10);
    const blogs = results.filter((r) => r.type === "blog").slice(0, 10);
    const comparisons = results.filter((r) => r.type === "comparison").slice(0, 10);
    const glossaries = results.filter((r) => r.type === "glossary").slice(0, 10);

    const getSectionMaxScore = (items: SearchResult[]) => {
      if (items.length === 0) return -1;
      return Math.max(...items.map((item) => item.score || 0));
    };

    const sections = [
      { items: calculators },
      { items: blogs },
      { items: comparisons },
      { items: glossaries },
    ];

    sections.sort((a, b) => getSectionMaxScore(b.items) - getSectionMaxScore(a.items));

    const flat: SearchResult[] = [];
    for (const section of sections) {
      flat.push(...section.items);
    }
    return flat;
  }, [results]);

  // 5. Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // 6. Navigation handler
  const handleSelect = (item: SearchResult) => {
    router.push(item.href);
    setQuery("");
    setDebouncedQuery("");
    setIsOpen(false);
    setIsMobileOverlayOpen(false);
    setActiveIndex(-1);
    
    // Blur inputs
    inputRef.current?.blur();
    mobileInputRef.current?.blur();
  };

  // 7. Keyboard Navigation (Arrow up/down, Enter, Escape)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || flatItems.length === 0) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((prev) => (prev < flatItems.length - 1 ? prev + 1 : 0));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : flatItems.length - 1));
        break;
      case "Enter":
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < flatItems.length) {
          handleSelect(flatItems[activeIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        inputRef.current?.blur();
        mobileInputRef.current?.blur();
        break;
    }
  };

  // Toggle mobile search drawer
  const openMobileOverlay = () => {
    setIsMobileOverlayOpen(true);
    // Delay focus to allow transition to render input
    setTimeout(() => {
      mobileInputRef.current?.focus();
      handleFocus();
    }, 100);
  };

  const closeMobileOverlay = () => {
    setIsMobileOverlayOpen(false);
    setQuery("");
    setDebouncedQuery("");
    setIsOpen(false);
  };

  return (
    <div className="global-search-wrapper" ref={searchContainerRef}>
      {/* Desktop Search Input */}
      <div 
        className="search-input-container desktop-only"
        role="combobox"
        aria-expanded={isOpen && query.trim().length >= 2}
        aria-haspopup="listbox"
        aria-controls="search-results-listbox"
      >
        <Search size={18} className="search-input-icon" />
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholders[placeholderIndex]}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          aria-label="Search across site calculators, guides, comparisons and glossary"
          className="search-input-field"
        />
        {query && (
          <button 
            onClick={() => setQuery("")} 
            className="search-clear-btn"
            aria-label="Clear search query"
          >
            <X size={16} />
          </button>
        )}

        {/* Dropdown Results for Desktop */}
        {isOpen && query.trim().length >= 2 && (
          <SearchResults
            results={results}
            searchQuery={query}
            activeIndex={activeIndex}
            onSelect={handleSelect}
            isLoading={isLoading}
          />
        )}
      </div>

      {/* Mobile Search Trigger Button */}
      <button
        onClick={openMobileOverlay}
        className="mobile-search-trigger-btn mobile-only"
        aria-label="Open search"
      >
        <Search size={22} />
      </button>

      {/* Mobile Full-Screen Overlay - Rendered via Portal */}
      {isMounted && isMobileOverlayOpen && createPortal(
        <div className="mobile-search-overlay anim-fade-in" role="dialog" aria-modal="true">
          <div className="mobile-search-header">
            <button 
              onClick={closeMobileOverlay} 
              className="mobile-search-back-btn"
              aria-label="Close search"
            >
              <ArrowLeft size={24} />
            </button>
            <div className="mobile-search-input-box">
              <input
                ref={mobileInputRef}
                type="text"
                placeholder={placeholders[placeholderIndex]}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                className="mobile-search-input"
                aria-label="Search field"
              />
              {query && (
                <button 
                  onClick={() => setQuery("")} 
                  className="mobile-search-clear-btn"
                  aria-label="Clear text"
                >
                  <X size={20} />
                </button>
              )}
            </div>
          </div>
          <div className="mobile-search-results-container">
            <SearchResults
              results={results}
              searchQuery={query}
              activeIndex={activeIndex}
              onSelect={handleSelect}
              isLoading={isLoading}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
